<!-- ============================================================
     PAYMENT RECEIPT PAGE
     ============================================================ -->
<div class="receipt-page">

  @if (isLoading()) {
    <div class="d-flex justify-content-center align-items-center" style="min-height:60vh;">
      <div class="spinner-border text-primary" role="status"></div>
    </div>
  }

  @if (!isLoading() && errorMsg()) {
    <div class="d-flex flex-column align-items-center justify-content-center" style="min-height:60vh;">
      <i class="bi bi-exclamation-triangle-fill text-danger" style="font-size: 3rem;"></i>
      <h5 class="mt-3">{{ errorMsg() }}</h5>
      <a routerLink="/payment" class="btn btn-primary-erp mt-3">Back to Payment</a>
    </div>
  }

  @if (!isLoading() && !errorMsg()) {
    @let r = receipt()!;

    <!-- Action Bar (hidden in print) -->
    <div class="action-bar no-print mb-4">
      <div>
        <h1 class="page-title">
          <i class="bi bi-receipt-cutoff me-2 text-primary" style="font-size:1.2rem;"></i>
          Payment Receipt
        </h1>
        <p class="page-subtitle">Transaction reference: <span class="font-monospace fw-semibold">{{ r.phonePeOrderId }}</span></p>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <button class="btn btn-outline-primary btn-sm d-flex align-items-center gap-2" (click)="print()">
          <i class="bi bi-printer-fill"></i> Print
        </button>
        <button class="btn btn-primary-erp btn-sm" (click)="downloadPdf()">
          <i class="bi bi-file-pdf-fill me-1"></i> Download PDF
        </button>
      </div>
    </div>

    <!-- Receipt Card -->
    <div class="receipt-card">

      <!-- ── Header ── -->
      <div class="receipt-header" [class.success]="isSuccess" [class.failed]="isFailed">
        <div class="rh-left">
          <div class="institution-logo">
            <!-- <i class="bi bi-mortarboard-fill"></i> -->
             <img src="/images/mw-logo-sm.png" width="48">
          </div>
          <div>
            <h3 class="institution-name">{{ r.institutionName }}</h3>
            <p class="institution-sub">Payment Receipt · Academic Year {{ r.academicYear }}</p>
          </div>
        </div>
        <div class="rh-status">
          @if (isSuccess) {
            <div class="status-pill success">
              <i class="bi bi-check-circle-fill"></i> Payment Successful
            </div>
          } @else if (isFailed) {
            <div class="status-pill failed">
              <i class="bi bi-x-circle-fill"></i> Payment Failed
            </div>
          } @else {
            <div class="status-pill pending">
              <i class="bi bi-clock-fill"></i> {{ state() }}
            </div>
          }
          <div class="receipt-no">Receipt No: <strong>{{ r.receiptNumber }}</strong></div>
        </div>
      </div>

      <!-- ── Success Animation ── -->
      @if (isSuccess) {
        <div class="success-banner no-print">
          <div class="sb-icon">
            <i class="bi bi-check-circle-fill"></i>
          </div>
          <div>
            <h5 class="mb-1">Payment Received Successfully!</h5>
            <p class="mb-0 small opacity-75">
              A confirmation email has been sent to <strong>{{ r.studentEmail }}</strong>.
              Keep this receipt for your records.
            </p>
          </div>
          <div class="sb-amount">{{ fmt(r.totalAmount) }}</div>
        </div>
      }

      @if (isFailed) {
        <div class="failed-banner no-print">
          <i class="bi bi-exclamation-triangle-fill fs-3"></i>
          <div>
            <h5 class="mb-1">Payment Not Completed</h5>
            <p class="mb-0 small">
              Your payment was not processed. No amount has been deducted from your account.
              Please <a routerLink="/payment" class="text-white fw-semibold">try again</a> or contact support.
            </p>
          </div>
        </div>
      }

      <!-- ── Body ── -->
      <div class="receipt-body">
        <div class="row g-4">

          <!-- Left: Transaction + Student Info -->
          <div class="col-md-6">

            <!-- Transaction Details -->
            <div class="info-section">
              <h6 class="info-section-title">
                <i class="bi bi-lightning-charge-fill text-warning me-2"></i>
                Transaction Details
              </h6>
              <table class="info-table">
                <tr>
                  <td>Order ID</td>
                  <td class="font-monospace small fw-semibold">{{ r.merchantOrderId }}</td>
                </tr>
                <tr>
                  <td>PhonePe Ref.</td>
                  <td class="font-monospace small fw-semibold">{{ r.phonePeOrderId }}</td>
                </tr>
                <tr>
                  <td>Receipt No.</td>
                  <td class="fw-semibold text-primary">{{ r.receiptNumber }}</td>
                </tr>
                <tr>
                  <td>Payment Mode</td>
                  <td>
                    <i class="bi {{ getMethodIcon(r.paymentMode) }} me-1 text-primary"></i>
                    {{ getMethodLabel(r.paymentMode) }}
                  </td>
                </tr>
                <tr>
                  <td>PhonePe State</td>
                  <td>
                    <span class="txn-status" [class.success]="isSuccess" [class.failed]="isFailed">
                      {{ r.state }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Date & Time</td>
                  <td class="small">{{ formatDate(r.paidAt) }}</td>
                </tr>
              </table>
            </div>

            <div class="info-section mt-4">
              <h6 class="info-section-title">
                <i class="bi bi-person-fill text-primary me-2"></i>
                Payer Details
              </h6>
              <table class="info-table">
                <tr>
                  <td>Name</td>
                  <td class="fw-semibold">{{ r.studentName }}</td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{{ r.studentEmail }}</td>
                </tr>
                <tr>
                  <td>Mobile</td>
                  <td>+91 {{ r.studentPhone }}</td>
                </tr>
                <tr>
                  <td>Program</td>
                  <td class="fw-semibold text-primary">{{ r.programName }}</td>
                </tr>
                <tr>
                  <td>Academic Year</td>
                  <td>{{ r.academicYear }}</td>
                </tr>
              </table>
            </div>

          </div>

          <!-- Right: Fee Breakdown -->
          <div class="col-md-6">
            <div class="info-section">
              <h6 class="info-section-title">
                <i class="bi bi-receipt-cutoff text-success me-2"></i>
                Payment Details
              </h6>
              <table class="fee-breakdown-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th class="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  @for (comp of r.feeComponents; track comp.label) {
                    <tr>
                      <td>{{ comp.label }}</td>
                      <td class="text-end">{{ comp.amount }}</td>
                    </tr>
                  }
                </tbody>
                <tfoot>
                  <tr class="subtotal-row">
                    <td>Subtotal</td>
                    <td class="text-end">{{ fmt(r.subtotal) }}</td>
                  </tr>
                  @if (r.taxAmount > 0) {
                    <tr>
                      <td>GST (18%)</td>
                      <td class="text-end">{{ fmt(r.taxAmount) }}</td>
                    </tr>
                  }
                  <tr>
                    <td colspan=2>&nbsp;</td>
                  </tr>
                  <tr class="grand-total-row">
                    <td>Total Paid</td>
                    <td class="text-end">{{ fmt(r.totalAmount) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- PhonePe Powered Badge -->
            <!-- <div class="pp-powered-badge mt-4">
              <i class="bi bi-telephone-fill me-2"></i>
              <div>
                <div class="ppb-main">Powered by PhonePe</div>
                <div class="ppb-sub">Secured · PCI DSS · RBI Approved</div>
              </div>
              <i class="bi bi-shield-lock-fill ms-auto text-success"></i>
            </div> -->

          </div>
        </div>

        <!-- Important Notes -->
        <div class="receipt-notes mt-4">
          <p class="mb-1 fw-semibold small"><i class="bi bi-info-circle-fill text-primary me-2"></i>Important Notes:</p>
          <ul class="notes-list">
            <li>This is a computer-generated receipt and does not require a physical signature.</li>
            <li>Application / Booking Fee is non-refundable once paid.</li>
            <li>Admission Fee is refundable within 30 days if admission is rejected by the institution.</li>
            <li>For any payment dispute, please quote the PhonePe Reference Number: <strong>{{ r.phonePeOrderId }}</strong></li>
            <li>Contact: <strong>{{erp_mgr.email_id}}</strong> | Helpline: <strong>{{erp_mgr.phone_no}}</strong></li>
          </ul>
        </div>

      </div>

      <!-- ── Footer ── -->
      <div class="receipt-footer">
        <div class="rf-left">
          <i class="bi bi-patch-check-fill text-success me-2"></i>
          This receipt is valid for all official purposes
        </div>
        <div class="rf-right">
          Generated on {{ formatDate(r.paidAt) }}
        </div>
      </div>

    </div>

    <!-- CTA (hidden in print) -->
    <!-- @if (isSuccess) {
      <div class="cta-section no-print mt-4">
        <div class="row g-3 justify-content-center">
          <div class="col-md-4">
            <a routerLink="/apply" class="cta-card">
              <i class="bi bi-person-plus-fill"></i>
              <div>
                <div class="cta-label">Continue Application</div>
                <div class="cta-sub">Complete your admission form</div>
              </div>
              <i class="bi bi-arrow-right ms-auto"></i>
            </a>
          </div>
          <div class="col-md-4">
            <a routerLink="/dashboard" class="cta-card">
              <i class="bi bi-speedometer2"></i>
              <div>
                <div class="cta-label">Go to Dashboard</div>
                <div class="cta-sub">View admission status</div>
              </div>
              <i class="bi bi-arrow-right ms-auto"></i>
            </a>
          </div>
          <div class="col-md-4">
            <button class="cta-card" (click)="downloadPdf()">
              <i class="bi bi-file-pdf-fill"></i>
              <div>
                <div class="cta-label">Download Receipt</div>
                <div class="cta-sub">Save PDF for records</div>
              </div>
              <i class="bi bi-arrow-right ms-auto"></i>
            </button>
          </div>
        </div>
      </div>
    } -->

    @if (isFailed) {
      <div class="cta-section no-print mt-4 text-center">
        <a routerLink="/payment" class="btn btn-pay-primary px-5 py-3">
          <i class="bi bi-arrow-counterclockwise me-2"></i> Retry Payment
        </a>
      </div>
    }

  }

</div>
